package br.com.htms.inspeccaocampo.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.DateRange
import androidx.compose.material.icons.filled.Home
import androidx.compose.material.icons.filled.Info
import androidx.compose.material.icons.filled.Person
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import br.com.htms.inspeccaocampo.data.RelSegDadosGerais
import br.com.htms.inspeccaocampo.data.RelSegDdsEntry
import br.com.htms.inspeccaocampo.data.RelSegGestaoSms
import br.com.htms.inspeccaocampo.data.RelSegMeses
import br.com.htms.inspeccaocampo.data.RelSegPayload
import br.com.htms.inspeccaocampo.data.RelSegTreinamento
import br.com.htms.inspeccaocampo.form.AppController
import br.com.htms.inspeccaocampo.shared.ui.components.CampoFotoRow
import br.com.htms.inspeccaocampo.ui.kit.Campo
import br.com.htms.inspeccaocampo.ui.kit.CartaoSecao
import br.com.htms.inspeccaocampo.ui.kit.Kpi
import br.com.htms.inspeccaocampo.ui.kit.LinhaKpis
import br.com.htms.inspeccaocampo.ui.kit.Par
import br.com.htms.inspeccaocampo.ui.kit.PortalButton
import br.com.htms.inspeccaocampo.ui.kit.PortalMenuItem
import br.com.htms.inspeccaocampo.ui.kit.PortalModulo
import br.com.htms.inspeccaocampo.ui.kit.PortalOutlinedButton
import br.com.htms.inspeccaocampo.ui.kit.Seletor
import br.com.htms.inspeccaocampo.ui.theme.PortalCores

private enum class AbaRelSeg(val rotulo: String, val icone: ImageVector) {
    RESUMO("Resumo", Icons.Filled.Home),
    DADOS("Dados gerais", Icons.Filled.Info),
    SMS("Gestão de SMS", Icons.Filled.Warning),
    DDS("DDS", Icons.Filled.DateRange),
    TREINAMENTOS("Treinamentos", Icons.Filled.Person)
}

private val CorFatal = Color(0xFF8E1B1B)
private val CorAcidente = Color(0xFFC62828)
private val CorLesao = Color(0xFFE07B00)
private val CorQuase = Color(0xFFF2B33D)

@Composable
fun SharedRelatorioSegurancaScreen(controller: AppController) {
    val form by controller.relSegForm.collectAsState()
    val obrasAtivas by controller.obrasAtivas.collectAsState()
    val salvando by controller.relSegSalvando.collectAsState()
    val baixando by controller.relSegBaixando.collectAsState()
    val readOnly = form.readOnly
    val aberto = !form.dossierId.isNullOrBlank()

    var aba by remember { mutableStateOf(AbaRelSeg.RESUMO) }

    LaunchedEffect(Unit) { controller.carregarObrasAtivas() }

    fun setPayload(p: RelSegPayload) = controller.atualizarRelSegPayload(p)
    val dg = form.payload.dadosGerais
    val sms = form.payload.gestaoSms
    val dds = form.payload.withDdsSize().dds
    val tre = form.payload.withTreinamentosSize().treinamentos

    val dgPreenchidos = listOf(dg.empresa, dg.cnpj, dg.grauRisco, dg.obra.ifBlank { form.obra }, dg.contrato, dg.vigencia, dg.cliente, dg.efetivoHtms, dg.efetivoTerceiro)
        .count { it.isNotBlank() }
    val smsPreenchidos = listOf(sms.apr, sms.pt, sms.dds, sms.treinamentos, sms.checklist).count { it.isNotBlank() }
    val ddsFeitos = dds.count { it.temaData.isNotBlank() }
    val ddsComFoto = dds.count { it.fotoRef.isNotBlank() }
    val treFeitos = tre.count { it.treinamento.isNotBlank() }

    val itens = AbaRelSeg.entries.map {
        val destaque = aberto && !readOnly && when (it) {
            AbaRelSeg.DADOS -> dgPreenchidos < 9
            AbaRelSeg.SMS -> smsPreenchidos < 5
            else -> false
        }
        PortalMenuItem(it.name, it.rotulo, it.icone, destaque = destaque)
    }

    PortalModulo(
        titulo = if (aberto) "Relatório de Segurança — ${form.obra} · ${RelSegMeses.rotulo(form.mes)}/${form.ano}"
        else "Relatório de Segurança",
        itens = itens,
        selecionado = aba.name,
        onSelect = { aba = AbaRelSeg.valueOf(it) },
        extraCabecalho = {
            BotaoPlanilha(controller, br.com.htms.inspeccaocampo.export.PlanilhaModulo.RELATORIO_SEGURANCA)
            if (aberto) {
                Spacer(Modifier.width(8.dp))
                PortalOutlinedButton(onClick = { controller.baixarRelSegPptx() }, enabled = !baixando) {
                    Text(if (baixando) "Gerando PPTX..." else "Baixar PPTX")
                }
                if (!readOnly) {
                    Spacer(Modifier.width(8.dp))
                    PortalButton(onClick = { controller.salvarRelSegDossier() }, enabled = !salvando) {
                        Text(if (salvando) "Salvando..." else "Salvar")
                    }
                }
            }
        }
    ) { largo ->
        CartaoPeriodoRelSeg(controller, form.obra, form.ano, form.mes, obrasAtivas.map { it.nome }, aberto, salvando, largo)
        if (!aberto) {
            Text(
                "Abra o relatório para preencher ${aba.rotulo}.",
                color = PortalCores.CinzaTexto
            )
            return@PortalModulo
        }
        if (readOnly) {
            AvisoRelSeg("Este relatório já foi salvo e está somente para leitura. Somente o admin pode alterar.", PortalCores.Vermelho)
        }
        when (aba) {
            AbaRelSeg.RESUMO -> ResumoRelSeg(
                largo, sms, dgPreenchidos, smsPreenchidos, ddsFeitos, ddsComFoto, treFeitos,
                pessoas = tre.sumOf { t -> t.numPessoas.filter { it.isDigit() }.toIntOrNull() ?: 0 },
                irPara = { aba = it }
            )
            AbaRelSeg.DADOS -> DadosGeraisRelSeg(dg, form.obra, readOnly, largo) { setPayload(form.payload.copy(dadosGerais = it)) }
            AbaRelSeg.SMS -> GestaoSmsRelSeg(sms, readOnly, largo) { setPayload(form.payload.copy(gestaoSms = it)) }
            AbaRelSeg.DDS -> DdsRelSeg(controller, dds, readOnly, largo) { setPayload(form.payload.copy(dds = it)) }
            AbaRelSeg.TREINAMENTOS -> TreinamentosRelSeg(controller, tre, readOnly, largo) { setPayload(form.payload.copy(treinamentos = it)) }
        }
        if (!readOnly) {
            PortalButton(onClick = { controller.salvarRelSegDossier() }, enabled = !salvando, modifier = Modifier.fillMaxWidth()) {
                Text(if (salvando) "Salvando..." else "Salvar relatório")
            }
        }
    }
}

@Composable
private fun CartaoPeriodoRelSeg(
    controller: AppController,
    obra: String,
    ano: Int,
    mes: Int,
    obras: List<String>,
    aberto: Boolean,
    salvando: Boolean,
    largo: Boolean
) {
    val opObras = (obras + obra).filter { it.isNotBlank() }.distinct().map { it to it }
    val opAnos = ((2024..2030) + ano).distinct().sorted().map { it.toString() to it.toString() }
    val opMeses = RelSegMeses.rotulos.mapIndexed { i, r -> (i + 1).toString() to r }
    CartaoSecao(
        titulo = "Obra e período",
        subtitulo = if (aberto) "Relatório aberto. Trocar a obra, o ano ou o mês fecha este relatório."
        else "Um relatório por obra e mês. Escolha e abra o relatório; depois de salvar, baixe o PPTX oficial."
    ) {
        val cObra: @Composable (Modifier) -> Unit = { m -> Seletor("Obra", opObras, obra, { controller.atualizarRelSegObra(it) }, m) }
        val cAno: @Composable (Modifier) -> Unit = { m ->
            Seletor("Ano", opAnos, ano.toString(), { v -> v.toIntOrNull()?.let { controller.atualizarRelSegAno(it) } }, m)
        }
        val cMes: @Composable (Modifier) -> Unit = { m ->
            Seletor("Mês", opMeses, mes.toString(), { v -> v.toIntOrNull()?.let { controller.atualizarRelSegMes(it) } }, m)
        }
        val botao: @Composable (Modifier) -> Unit = { m ->
            PortalButton(onClick = { controller.abrirOuCriarRelSegDossier() }, enabled = obra.isNotBlank() && !salvando, modifier = m) {
                Text(if (aberto) "Recarregar" else "Abrir relatório")
            }
        }
        if (largo) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp), verticalAlignment = Alignment.CenterVertically) {
                cObra(Modifier.weight(2f))
                cAno(Modifier.weight(1f))
                cMes(Modifier.weight(1f))
                botao(Modifier)
            }
        } else {
            cObra(Modifier.fillMaxWidth())
            Par(true, cAno, cMes)
            botao(Modifier.fillMaxWidth())
        }
    }
}

@Composable
private fun AvisoRelSeg(texto: String, cor: Color) {
    Text(
        texto,
        color = cor,
        style = MaterialTheme.typography.bodyMedium,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier
            .fillMaxWidth()
            .background(cor.copy(alpha = 0.08f), RoundedCornerShape(10.dp))
            .padding(horizontal = 14.dp, vertical = 10.dp)
    )
}

// ---------- Resumo ----------

@Composable
private fun ResumoRelSeg(
    largo: Boolean,
    sms: RelSegGestaoSms,
    dgPreenchidos: Int,
    smsPreenchidos: Int,
    ddsFeitos: Int,
    ddsComFoto: Int,
    treFeitos: Int,
    pessoas: Int,
    irPara: (AbaRelSeg) -> Unit
) {
    LinhaKpis(largo) { m ->
        Kpi(
            "Dados gerais", "$dgPreenchidos/9", Icons.Filled.Info, m,
            alerta = dgPreenchidos < 9, cor = if (dgPreenchidos == 9) PortalCores.Verde else null,
            legenda = "campos preenchidos", onClick = { irPara(AbaRelSeg.DADOS) }
        )
        Kpi(
            "Gestão de SMS", "$smsPreenchidos/5", Icons.Filled.CheckCircle, m,
            alerta = smsPreenchidos < 5, cor = if (smsPreenchidos == 5) PortalCores.Verde else null,
            legenda = "resumos preenchidos", onClick = { irPara(AbaRelSeg.SMS) }
        )
        Kpi(
            "DDS registrados", ddsFeitos.toString(), Icons.Filled.DateRange, m,
            legenda = "$ddsComFoto com foto", onClick = { irPara(AbaRelSeg.DDS) }
        )
        Kpi(
            "Treinamentos", "$treFeitos/3", Icons.Filled.Person, m,
            legenda = "$pessoas pessoa(s) impactada(s)", onClick = { irPara(AbaRelSeg.TREINAMENTOS) }
        )
    }
    CartaoSecao(
        titulo = "Pirâmide de acidentes",
        subtitulo = "Desvios gerais (soma da pirâmide, calculada no PPTX): ${listOfNotNull(sms.acidentesFatais, sms.acidentes, sms.lesoes, sms.quaseAcidentes).sum()}",
        acoes = { PortalOutlinedButton(onClick = { irPara(AbaRelSeg.SMS) }) { Text("Editar") } }
    ) {
        PiramideRelSeg(sms)
    }
}

@Composable
private fun PiramideRelSeg(sms: RelSegGestaoSms) {
    val niveis = listOf(
        Triple("Acidentes fatais", sms.acidentesFatais, CorFatal),
        Triple("Acidentes", sms.acidentes, CorAcidente),
        Triple("Lesões", sms.lesoes, CorLesao),
        Triple("Quase acidentes", sms.quaseAcidentes, CorQuase)
    )
    Column(Modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(6.dp)) {
        niveis.forEachIndexed { i, (rotulo, valor, cor) ->
            Box(
                Modifier
                    .fillMaxWidth(0.4f + 0.2f * i)
                    .background(cor, RoundedCornerShape(8.dp))
                    .padding(vertical = 10.dp, horizontal = 12.dp),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    "$rotulo: ${valor?.toString() ?: "—"}",
                    color = if (cor == CorQuase) PortalCores.Navy else Color.White,
                    fontWeight = FontWeight.Bold
                )
            }
        }
    }
}

// ---------- Dados gerais ----------

@Composable
private fun DadosGeraisRelSeg(
    dg: RelSegDadosGerais,
    obraForm: String,
    ro: Boolean,
    largo: Boolean,
    set: (RelSegDadosGerais) -> Unit
) {
    CartaoSecao("Dados gerais", subtitulo = "Aparecem no slide de identificação do PPTX.") {
        Par(largo, { m -> Campo("Empresa", dg.empresa, somenteLeitura = ro, modifier = m) { set(dg.copy(empresa = it)) } },
            { m -> Campo("CNPJ", dg.cnpj, somenteLeitura = ro, modifier = m) { set(dg.copy(cnpj = it)) } })
        Par(largo, { m -> Campo("Obra", dg.obra.ifBlank { obraForm }, somenteLeitura = ro, modifier = m) { set(dg.copy(obra = it)) } },
            { m -> Campo("Cliente", dg.cliente, somenteLeitura = ro, modifier = m) { set(dg.copy(cliente = it)) } })
        Par(largo, { m -> Campo("Contrato", dg.contrato, somenteLeitura = ro, modifier = m) { set(dg.copy(contrato = it)) } },
            { m -> Campo("Vigência do contrato", dg.vigencia, somenteLeitura = ro, modifier = m) { set(dg.copy(vigencia = it)) } })
        Par(largo, { m -> Campo("Grau de risco", dg.grauRisco, somenteLeitura = ro, modifier = m) { set(dg.copy(grauRisco = it)) } },
            { m ->
                Row(m, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                    Campo("Efetivo HTMS", dg.efetivoHtms, numero = true, somenteLeitura = ro, modifier = Modifier.weight(1f)) { set(dg.copy(efetivoHtms = it)) }
                    Campo("Efetivo terceiro", dg.efetivoTerceiro, numero = true, somenteLeitura = ro, modifier = Modifier.weight(1f)) { set(dg.copy(efetivoTerceiro = it)) }
                }
            })
    }
}

// ---------- Gestão de SMS ----------

@Composable
private fun GestaoSmsRelSeg(sms: RelSegGestaoSms, ro: Boolean, largo: Boolean, set: (RelSegGestaoSms) -> Unit) {
    CartaoSecao("Resumos da gestão de SMS", subtitulo = "Texto curto de cada item, como sai no slide de Gestão de SMS.") {
        Par(largo, { m -> Campo("APR", sms.apr, linhas = 3, somenteLeitura = ro, modifier = m) { set(sms.copy(apr = it)) } },
            { m -> Campo("PT", sms.pt, linhas = 3, somenteLeitura = ro, modifier = m) { set(sms.copy(pt = it)) } })
        Par(largo, { m -> Campo("DDS", sms.dds, linhas = 3, somenteLeitura = ro, modifier = m) { set(sms.copy(dds = it)) } },
            { m -> Campo("Treinamentos", sms.treinamentos, linhas = 3, somenteLeitura = ro, modifier = m) { set(sms.copy(treinamentos = it)) } })
        Campo("Checklist", sms.checklist, linhas = 3, somenteLeitura = ro) { set(sms.copy(checklist = it)) }
    }
    fun num(v: String) = v.filter { it.isDigit() }.toIntOrNull()
    CartaoSecao("Pirâmide de acidentes", subtitulo = "Os desvios gerais são a soma destes quatro números.") {
        Par(largo, { m -> Campo("Acidentes fatais", sms.acidentesFatais?.toString().orEmpty(), numero = true, somenteLeitura = ro, modifier = m) { set(sms.copy(acidentesFatais = num(it))) } },
            { m -> Campo("Acidentes", sms.acidentes?.toString().orEmpty(), numero = true, somenteLeitura = ro, modifier = m) { set(sms.copy(acidentes = num(it))) } })
        Par(largo, { m -> Campo("Lesões", sms.lesoes?.toString().orEmpty(), numero = true, somenteLeitura = ro, modifier = m) { set(sms.copy(lesoes = num(it))) } },
            { m -> Campo("Quase acidentes", sms.quaseAcidentes?.toString().orEmpty(), numero = true, somenteLeitura = ro, modifier = m) { set(sms.copy(quaseAcidentes = num(it))) } })
        PiramideRelSeg(sms)
    }
}

// ---------- DDS ----------

@Composable
private fun DdsRelSeg(
    controller: AppController,
    dds: List<RelSegDdsEntry>,
    ro: Boolean,
    largo: Boolean,
    set: (List<RelSegDdsEntry>) -> Unit
) {
    val ultimoPreenchido = dds.indexOfLast { it.temaData.isNotBlank() || it.fotoRef.isNotBlank() }
    var todos by remember { mutableStateOf(false) }
    val visiveis = if (todos || ro) {
        if (ro) (ultimoPreenchido + 1).coerceAtLeast(1) else dds.size
    } else (ultimoPreenchido + 2).coerceIn(4, dds.size)
    CartaoSecao(
        "Registros de DDS",
        subtitulo = "Até 30 por mês: tema e data, com foto opcional. ${dds.count { it.temaData.isNotBlank() }} registrado(s).",
        acoes = {
            if (!ro && visiveis < dds.size) {
                PortalOutlinedButton(onClick = { todos = true }) { Text("Mostrar os 30") }
            }
        }
    ) {
        (0 until visiveis).chunked(if (largo) 2 else 1).forEach { linha ->
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                linha.forEach { i ->
                    ItemDds(controller, i, dds[i], ro, Modifier.weight(1f)) { novo ->
                        set(dds.toMutableList().also { it[i] = novo })
                    }
                }
                if (linha.size == 1 && largo) Spacer(Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun ItemDds(
    controller: AppController,
    i: Int,
    e: RelSegDdsEntry,
    ro: Boolean,
    modifier: Modifier,
    set: (RelSegDdsEntry) -> Unit
) {
    Column(
        modifier
            .background(PortalCores.FundoPainel, RoundedCornerShape(12.dp))
            .padding(12.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text("DDS ${i + 1}", color = PortalCores.Navy, fontWeight = FontWeight.Bold, modifier = Modifier.weight(1f))
            if (e.fotoRef.isNotBlank()) {
                Icon(Icons.Filled.CheckCircle, contentDescription = null, tint = PortalCores.Verde, modifier = Modifier.size(18.dp))
                Spacer(Modifier.width(4.dp))
                Text("com foto", color = PortalCores.Verde, style = MaterialTheme.typography.bodySmall)
            }
        }
        Campo("Tema e data", e.temaData, somenteLeitura = ro) { set(e.copy(temaData = it)) }
        if (!ro) {
            CampoFotoRow(
                label = if (e.fotoRef.isBlank()) "Foto do DDS" else "Foto anexada",
                obrigatorio = false,
                quantidadeFotos = if (e.fotoRef.isBlank()) 0 else 1,
                onTirarFoto = { controller.adicionarFotoRelSegDds(i, fromGallery = false) },
                onAdicionarMais = { controller.adicionarFotoRelSegDds(i, fromGallery = false) },
                onAbrirLista = { },
                onGaleria = { controller.adicionarFotoRelSegDds(i, fromGallery = true) }
            )
            if (e.fotoRef.isNotBlank()) {
                PortalOutlinedButton(onClick = { controller.removerFotoRelSegDds(i) }, modifier = Modifier.fillMaxWidth()) {
                    Text("Remover foto")
                }
            }
        }
    }
}

// ---------- Treinamentos ----------

@Composable
private fun TreinamentosRelSeg(
    controller: AppController,
    tre: List<RelSegTreinamento>,
    ro: Boolean,
    largo: Boolean,
    set: (List<RelSegTreinamento>) -> Unit
) {
    tre.take(3).forEachIndexed { i, t ->
        fun up(novo: RelSegTreinamento) = set(tre.toMutableList().also { it[i] = novo })
        CartaoSecao(
            "Treinamento ${i + 1}",
            subtitulo = if (t.treinamento.isBlank()) "Não preenchido. Cada treinamento vira um slide com até 4 fotos (2×2)."
            else "${t.fotos.count { it.isNotBlank() }}/4 foto(s)"
        ) {
            Par(largo, { m -> Campo("Treinamento", t.treinamento, somenteLeitura = ro, modifier = m) { up(t.copy(treinamento = it)) } },
                { m ->
                    Row(m, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                        Campo("Data", t.data, somenteLeitura = ro, modifier = Modifier.weight(1f)) { up(t.copy(data = it)) }
                        Campo("Nº pessoas impactadas", t.numPessoas, numero = true, somenteLeitura = ro, modifier = Modifier.weight(1f)) { up(t.copy(numPessoas = it)) }
                    }
                })
            Campo("Nomes", t.nomes, linhas = 2, somenteLeitura = ro) { up(t.copy(nomes = it)) }
            Campo("Descrição", t.descricao, linhas = 3, somenteLeitura = ro) { up(t.copy(descricao = it)) }
            if (!ro) {
                Text("Fotos (grid 2×2 no PPTX)", color = PortalCores.Navy, fontWeight = FontWeight.SemiBold)
                t.withFotosSize().fotos.chunked(2).forEachIndexed { linha, par ->
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                        par.forEachIndexed { k, ref ->
                            val j = linha * 2 + k
                            Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                                CampoFotoRow(
                                    label = "Foto ${j + 1}",
                                    obrigatorio = false,
                                    quantidadeFotos = if (ref.isBlank()) 0 else 1,
                                    onTirarFoto = { controller.adicionarFotoRelSegTreinamento(i, j, fromGallery = false) },
                                    onAdicionarMais = { controller.adicionarFotoRelSegTreinamento(i, j, fromGallery = false) },
                                    onAbrirLista = { },
                                    onGaleria = { controller.adicionarFotoRelSegTreinamento(i, j, fromGallery = true) }
                                )
                                if (ref.isNotBlank()) {
                                    PortalOutlinedButton(onClick = { controller.removerFotoRelSegTreinamento(i, j) }, modifier = Modifier.fillMaxWidth()) {
                                        Text("Remover foto ${j + 1}")
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
